import {
  GeckoAssetModelType,
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  assetModelFormFields,
  GeckoAssetModel,
} from 'types/entries/geckoAssetModel'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import {
  convertPlatesToPolygons,
  convertPolygonsToWeldMap,
  GeckoComponentRect,
  GeckoCourseJSON,
  GeckoFixedSensorJSON,
  GeckoHorizontalWeldJSON,
  GeckoManwayJSON,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoNozzleJSON,
  GeckoPlateJSON,
  GeckoVerticalWeldJSON,
  PipeSectionAssetModelJSON,
  RolodexEntryJson,
  WeldedPolygon,
  WrappingDirection,
} from 'types/index'
import { RolodexEntryPutJson } from 'types/rolodex/rolodexEntries'
import { assetModelPrecision } from 'utils/constants'
import { getDropdownOptionsForEnum } from 'utils/enum'
import { parseDate, parseNumber } from 'utils/parsingHelpers'
import { optionalNumberSchema, optionalStringSchema } from 'utils/zodHelpers'

import { InsulatedOptions } from './assetModelHelpers'
import { BaseAssetModelData } from './baseAssetModelData'

export type GeckoPipelineAssetModelJSON = {
  name: string
  type: GeckoAssetModelType.Piping
  installation_date: string | undefined
  components: Record<string, PipeSectionAssetModelJSON>
  effective_date: string
  compass_orientation: number | undefined
  plant_north_compass_orientation: number | undefined
  insulated: string | undefined
  contents: string | undefined
  contents_ph: number | undefined
  surface_temperature: number | undefined
  operating_pressure: number | undefined
}

export const PipelineAvailableComponents: GeckoComponentModelType[] = [
  GeckoComponentModelType.PipeSection,
  GeckoComponentModelType.PipeElbow,
  GeckoComponentModelType.PipeTJoint,
  GeckoComponentModelType.PipeEccentricReducer,
]

export class GeckoPipelineAssetModel extends GeckoAssetModel<
  BaseAssetModelData & {
    insulated: InsulatedOptions | undefined
    contents: string | undefined
    contentsPH: number | undefined
    surfaceTemperature: GeckoMeasurement | undefined
    operatingPressure: GeckoMeasurement | undefined
  }
> {
  private constructor(json: RolodexEntryJson) {
    super(json)
    const recordData = json.data as Record<string, unknown>
    const parsedSurfaceTemperature = parseNumber(recordData.surface_temperature)
    const parsedOperatingPressure = parseNumber(recordData.operating_pressure)

    this.data.insulated = recordData.insulated
      ? (recordData.insulated as InsulatedOptions)
      : undefined
    this.data.contents = recordData.contents as string
    this.data.contentsPH = parseNumber(recordData.contents_ph)
    if (parsedSurfaceTemperature !== undefined) {
      this.data.surfaceTemperature = GeckoMeasurement.CreateFromValue(
        parsedSurfaceTemperature,
        GeckoMeasurementUnit.Celsius
      )
    }
    if (parsedOperatingPressure !== undefined) {
      this.data.operatingPressure = GeckoMeasurement.CreateFromValue(
        parsedOperatingPressure,
        GeckoMeasurementUnit.Megapascals
      )
    }
  }

  public static CreateFromJSON(
    json: RolodexEntryJson
  ): GeckoPipelineAssetModel {
    return new GeckoPipelineAssetModel(json)
  }

  public asJSON(): GeckoPipelineAssetModelJSON {
    return generatePipelineAssetModel(
      this.data.name,
      this.data.installation_date,
      this.raw.data.components as Record<string, PipeSectionAssetModelJSON>,
      this.data.effective_date,
      this.data.compass_orientation,
      this.data.plant_north_compass_orientation,
      this.data.insulated as string,
      this.data.contents as string,
      this.data.contentsPH as number,
      this.data.surfaceTemperature as GeckoMeasurement,
      this.data.operatingPressure as GeckoMeasurement
    )
  }
}

export function generatePipelineAssetModel(
  name: string,
  installation_date: Date | undefined,
  components: Record<string, PipeSectionAssetModelJSON>,
  effective_date: Date,
  compass_orientation: number | undefined,
  plant_north_compass_orientation: number | undefined,
  insulated: string | undefined,
  contents: string | undefined,
  contents_ph: number | undefined,
  surface_temperature: GeckoMeasurement | undefined,
  operating_pressure: GeckoMeasurement | undefined
): GeckoPipelineAssetModelJSON {
  const pipelineSurfaceTemperature =
    surface_temperature !== undefined
      ? surface_temperature
          .convertTo(GeckoMeasurementUnit.Celsius)
          .toPrecision(assetModelPrecision).num
      : undefined
  const pipelineOperatingPressure =
    operating_pressure !== undefined
      ? operating_pressure
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined
  const pipelineInstallationDate = installation_date
    ? installation_date.toISOString().split('T')[0]
    : undefined

  const pipelineEffectiveDate = effective_date.toISOString().split('T')[0]
  return {
    name: name,
    type: GeckoAssetModelType.Piping,
    installation_date: pipelineInstallationDate,
    components: components,
    effective_date: pipelineEffectiveDate,
    compass_orientation: compass_orientation,
    plant_north_compass_orientation: plant_north_compass_orientation,
    insulated: insulated,
    contents: contents,
    contents_ph: contents_ph,
    surface_temperature: pipelineSurfaceTemperature,
    operating_pressure: pipelineOperatingPressure,
  }
}

export const pipingModelFormFields = (
  assetModelJSON: GeckoPipelineAssetModelJSON
) => {
  const assetModelfields = assetModelFormFields(assetModelJSON)
  const pipelineModelFormFields = [
    {
      dataKey: 'insulated',
      label: 'Insulated',
      value: assetModelJSON.insulated,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      options: getDropdownOptionsForEnum(InsulatedOptions),
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
    },
    {
      dataKey: 'contents',
      label: 'Contents',
      value: assetModelJSON.contents as string,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'contents_ph',
      label: 'Contents PH',
      value: assetModelJSON.contents_ph,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'surface_temperature',
      label: 'Surface Temperature',
      value: assetModelJSON.surface_temperature,
      unit_magnitude: UnitMagnitude.FahrenheitOrCelsius,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'operating_pressure',
      label: 'Operating Pressure',
      value: assetModelJSON.operating_pressure,
      unit_magnitude: UnitMagnitude.PsiOrMPa,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
  return [...assetModelfields, ...pipelineModelFormFields]
}

export const PipelineJSONFromFormFields = (
  assetModelJSON: GeckoPipelineAssetModelJSON,
  formFields: FormField[]
) => {
  const parsedSurfaceTemperature = parseNumber(
    getFormFieldByDataKey(formFields, 'surface_temperature')?.value
  )
  const parsedOperatingPressure = parseNumber(
    getFormFieldByDataKey(formFields, 'operating_pressure')?.value
  )
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  const contents = getFormFieldByDataKey(formFields, 'contents')
  const insulated = getFormFieldByDataKey(formFields, 'insulated')
  const contentsPH = getFormFieldByDataKey(formFields, 'contents_ph')
  return generatePipelineAssetModel(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    assetModelJSON.components,
    parseDate(assetModelJSON.effective_date) as Date,
    parseNumber(
      getFormFieldByDataKey(formFields, 'compass_orientation')?.value
    ),
    parseNumber(
      getFormFieldByDataKey(formFields, 'plant_north_compass_orientation')
        ?.value
    ),
    insulated?.value !== InsulatedOptions.Unknown
      ? (insulated?.value as string)
      : undefined,
    contents?.value ? (contents?.value as string) : undefined,
    parseNumber(contentsPH?.value),
    parsedSurfaceTemperature !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedSurfaceTemperature,
          GeckoMeasurementUnit.Celsius
        )
      : undefined,
    parsedOperatingPressure !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedOperatingPressure,
          GeckoMeasurementUnit.Megapascals
        )
      : undefined
  )
}

export function regeneratePipingAssetModel(
  assetModel: GeckoAssetModel
): RolodexEntryPutJson {
  const rawAssetModel = assetModel.raw

  if (!rawAssetModel.data.components) {
    throw new Error(
      'Unable to regenerate this Asset Model! Components seem to be undefined.'
    )
  }

  const regeneratedComponents = {} as Record<string, unknown>
  for (const [componentID, component] of Object.entries(
    rawAssetModel.data.components
  )) {
    switch (component.type) {
      case GeckoComponentModelType.PipeSection:
        regeneratedComponents[componentID] =
          regeneratePipeSectionComponent(component)
        break
      default:
        throw new Error('Unable to determine this type of Piping Component!')
    }
  }

  assetModel.raw.data.components = regeneratedComponents

  return assetModel.raw
}

export function regeneratePipeSectionComponent(
  pipeSection: Record<string, unknown>
): Record<string, unknown> {
  const pipeFeatures = pipeSection.features as Record<
    string,
    | GeckoPlateJSON
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >

  // If there are no features on the asset model, there's nothing to regenerate
  if (Object.keys(pipeFeatures).length === 0) {
    return pipeSection
  }

  const pipePlates = new Map<string, GeckoPlateJSON>()
  const pipeOther = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >()

  for (const [featureID, feature] of Object.entries(pipeFeatures)) {
    if (!feature.type) {
      console.error(
        'Unable to regenerate some features, as feature.type is missing!'
      )
      continue
    }

    switch (feature.type) {
      case GeckoFeatureModelType.Plate:
        pipePlates.set(featureID, feature as GeckoPlateJSON)
        break
      case GeckoFeatureModelType.VerticalWeld:
      case GeckoFeatureModelType.HorizontalWeld:
        continue
      default:
        pipeOther.set(featureID, feature)
    }
  }

  const componentBoundsJSON = pipeSection.component_bounds as Record<
    string,
    number
  >

  const componentBounds = GeckoComponentRect.CreateFromXYs(
    pipeSection.id as string,
    componentBoundsJSON.x_start as number,
    componentBoundsJSON.y_start as number,
    componentBoundsJSON.x_end as number,
    componentBoundsJSON.y_end as number,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
  const wrappingDirection = WrappingDirection.X
  const polygonArray: Array<WeldedPolygon> = []

  convertPlatesToPolygons(
    [...pipePlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertPolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )

  const features = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
    | GeckoPlateJSON
    | GeckoCourseJSON
  >([...pipePlates.entries(), ...welds.entries(), ...pipeOther.entries()])

  pipeSection.features = Object.fromEntries(features) as Record<string, unknown>

  return pipeSection
}
